import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "@/lib/react-router-dom-shim";
import { Breadcrumb } from "@/components/Breadcrumb";
import { supabase } from "@/integrations/supabase/client";
import { HotelDetailLoading } from "@/components/HotelDetailLoading";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EnhancedImageGallery } from "@/components/EnhancedImageGallery";
import { HotelAmenities } from "@/components/HotelAmenities";
import { HotelManualInfoSection } from "@/components/HotelManualInfoSection";
import { HotelStaticFAQ } from "@/components/HotelStaticFAQ";
import { HotelStaticMap } from "@/components/HotelStaticMap";
import { LazyInteractiveHotelMap } from "@/components/LazyInteractiveHotelMap";
import { SearchEditModal } from "@/components/SearchEditModal";
import { NoAvailabilityModal } from "@/components/NoAvailabilityModal";
import { useToast } from "@/hooks/use-toast";
import { 
  Star, MapPin, Calendar, Users, Wifi, Car, Coffee, Waves, Search,
  Bed, UtensilsCrossed, Dumbbell, Sparkles, TreePine, Mountain, 
  Plane, Clock, Heart, Shield, Baby, Accessibility, ParkingCircle, 
  Umbrella, Landmark, Info, Building2, ChevronDown, ChevronUp
} from "lucide-react";
import { cleanHtmlText } from "@/utils/textUtils";
import { extractAmenitiesFromText } from "@/utils/hotelAmenities";
import { useTranslation } from "react-i18next";
import { SchemaOrg, getHotelDetailSchema, getBreadcrumbSchema, getOrganizationSchema, getWebSiteSchema, getFAQSchema } from "@/components/seo/SchemaOrg";
import { HreflangTags } from "@/components/seo/HreflangTags";
import { HotelStaticDestinationLink } from "@/components/HotelStaticDestinationLink";
import { useLocale } from "@/contexts/LocaleContext";
import { LocaleKey } from "@/i18n";
import { getLocalizedUrl } from "@/hooks/useSeoMeta";
import { useHotelManualInfo } from "@/hooks/useHotelManualInfo";
import { useHotelManualFaq } from "@/hooks/useHotelManualFaq";
import { useReverseGeocode } from "@/hooks/useReverseGeocode";
import { format, addDays } from "date-fns";

interface HotelStaticData {
  id: string;
  hotel_id: string;
  slug: string;
  name: string;
  name_slug: string;
  star_rating: number | null;
  address: string | null;
  city: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  phone: string | null;
  category: string | null;
  description_es: string | null;
  description_en: string | null;
  description_pt: string | null;
  images: string[];
  amenities: string[];
  thumb_url: string | null;
  destination_id: string | null;
  updated_at: string | null;
}

const HotelStaticPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { locale } = useLocale();
  
  const { toast } = useToast();
  
  const [hotel, setHotel] = useState<HotelStaticData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isNoAvailabilityModalOpen, setIsNoAvailabilityModalOpen] = useState(false);
  const [pendingSearchParams, setPendingSearchParams] = useState<{
    checkIn: string;
    numNights: number;
    adults: number;
    childrenAges: number[];
  } | null>(null);

  // Fetch manual info, FAQ, and reverse geocode data
  const { manualInfo } = useHotelManualInfo(hotel?.hotel_id);
  const { faqItems, getLocalizedQuestion, getLocalizedAnswer } = useHotelManualFaq(hotel?.hotel_id);
  const { data: geocodeData } = useReverseGeocode(hotel?.latitude, hotel?.longitude);

  useEffect(() => {
    if (!slug) {
      navigate('/hoteles');
      return;
    }
    fetchHotelStatic();
  }, [slug]);

  // Increment visit count when page loads
  useEffect(() => {
    if (hotel?.hotel_id) {
      const incrementVisit = async () => {
        try {
          await supabase.functions.invoke('increment-hotel-visit', {
            body: { hotelId: hotel.hotel_id }
          });
        } catch (error) {
          console.error('Error incrementing visit count:', error);
        }
      };
      incrementVisit();
    }
  }, [hotel?.hotel_id]);

  const fetchHotelStatic = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('hotel_static_pages')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error || !data) {
        console.error('Hotel not found:', error);
        navigate('/hoteles');
        return;
      }

      const imagesArray: string[] = [];
      if (Array.isArray(data.images)) {
        data.images.forEach((img: unknown) => {
          if (typeof img === 'string') imagesArray.push(img);
        });
      }

      const amenitiesArray: string[] = [];
      if (Array.isArray(data.amenities)) {
        data.amenities.forEach((a: unknown) => {
          if (typeof a === 'string') amenitiesArray.push(a);
        });
      }

      const parsedData: HotelStaticData = {
        id: data.id,
        hotel_id: data.hotel_id,
        slug: data.slug,
        name: data.name,
        name_slug: data.name_slug,
        star_rating: data.star_rating,
        address: data.address,
        city: data.city,
        country: data.country,
        latitude: data.latitude ? Number(data.latitude) : null,
        longitude: data.longitude ? Number(data.longitude) : null,
        phone: data.phone,
        category: data.category,
        description_es: data.description_es,
        description_en: data.description_en,
        description_pt: data.description_pt,
        images: imagesArray,
        amenities: amenitiesArray,
        thumb_url: data.thumb_url,
        destination_id: data.destination_id || null,
        updated_at: data.updated_at || null,
      };

      setHotel(parsedData);
    } catch (error) {
      console.error('Error fetching hotel:', error);
      navigate('/hoteles');
    } finally {
      setIsLoading(false);
    }
  };

  // Get localized description
  const description = useMemo(() => {
    if (!hotel) return '';
    const lang = i18n.language;
    if (lang.startsWith('pt')) return hotel.description_pt || hotel.description_es || '';
    if (lang.startsWith('en')) return hotel.description_en || hotel.description_es || '';
    return hotel.description_es || '';
  }, [hotel, i18n.language]);

  const cleanedDescription = cleanHtmlText(description);

  // Extract hotel name without location suffix for cleaner FAQ text
  const cleanHotelName = useMemo(() => {
    const fullName = hotel?.name || '';
    // Remove location suffix like "(City, State, Country)" if present
    return fullName.replace(/\s*\([^)]+\)\s*$/, '').trim();
  }, [hotel?.name]);
  // Process images for gallery
  const images = useMemo(() => {
    if (!hotel) return [];
    return hotel.images.map(url => ({ url, title: '' }));
  }, [hotel]);

  // Extract amenities from description
  const amenityCategories = useMemo(() => {
    if (!hotel) return [];
    return extractAmenitiesFromText(description);
  }, [hotel, description]);

  const hasCoordinates = hotel?.latitude && hotel?.longitude;

  // Property highlights
  const propertyHighlights = useMemo(() => {
    const highlights = [];
    const amenityText = (hotel?.amenities || []).join(' ').toLowerCase() + ' ' + description.toLowerCase();
    
    if (amenityText.includes('wifi') || amenityText.includes('internet')) {
      highlights.push({ icon: Wifi, label: 'WiFi' });
    }
    if (amenityText.includes('parking') || amenityText.includes('estacion') || amenityText.includes('aparcamiento')) {
      highlights.push({ icon: Car, label: t('hotelDetail.parking') || 'Parking' });
    }
    if (amenityText.includes('breakfast') || amenityText.includes('desayuno') || amenityText.includes('café da manhã')) {
      highlights.push({ icon: Coffee, label: t('hotelDetail.breakfast') || 'Breakfast' });
    }
    if (amenityText.includes('pool') || amenityText.includes('piscina')) {
      highlights.push({ icon: Waves, label: t('hotelDetail.pool') || 'Pool' });
    }
    return highlights;
  }, [hotel?.amenities, description, t]);

  // Prepare FAQ items for schema
  const faqSchemaItems = useMemo(() => {
    return faqItems.map(item => ({
      question: getLocalizedQuestion(item),
      answer: getLocalizedAnswer(item)
    }));
  }, [faqItems, getLocalizedQuestion, getLocalizedAnswer]);

  // Schema.org data
  const hotelSchema = useMemo(() => {
    if (!hotel) return null;
    const manualPhotos = manualInfo?.photos || [];
    const allImages = [...manualPhotos, ...hotel.images].filter(Boolean);
    
    return getHotelDetailSchema({
      name: hotel.name,
      description: cleanedDescription,
      url: typeof window !== 'undefined' ? window.location.href : `https://www.foradarota.com/hotel/${hotel.slug}`,
      images: allImages,
      starRating: hotel.star_rating || undefined,
      address: hotel.address || undefined,
      city: hotel.city || undefined,
      postalCode: geocodeData?.postalCode || undefined,
      addressLocality: geocodeData?.addressLocality || undefined,
      addressRegion: geocodeData?.addressRegion || undefined,
      phone: hotel.phone || undefined,
      latitude: hotel.latitude || undefined,
      longitude: hotel.longitude || undefined,
      rooms: [],
      amenities: hotel.amenities,
      petsAllowed: manualInfo?.is_pet_friendly ?? undefined,
    }, locale as LocaleKey);
  }, [hotel, cleanedDescription, locale, manualInfo, geocodeData]);

  const baseUrl = getLocalizedUrl(locale as LocaleKey, '/');
  const breadcrumbSchema = useMemo(() => {
    if (!hotel) return null;
    return getBreadcrumbSchema([
      { name: t('nav.home'), url: baseUrl },
      { name: t('nav.hotels'), url: getLocalizedUrl(locale as LocaleKey, '/hoteles') },
      { name: hotel.name }
    ], typeof window !== 'undefined' ? window.location.href : `https://www.foradarota.com/hotel/${hotel.slug}`);
  }, [hotel, t, locale, baseUrl]);

  const organizationSchema = getOrganizationSchema(locale as LocaleKey);
  const webSiteSchema = getWebSiteSchema(locale as LocaleKey);
  const faqSchema = faqSchemaItems.length > 0 ? getFAQSchema(faqSchemaItems, typeof window !== 'undefined' ? window.location.href : 'https://www.foradarota.com') : null;

  // Default search values for the modal
  const defaultCheckIn = format(addDays(new Date(), 7), 'yyyy-MM-dd');
  const defaultNumNights = '7';
  const defaultAdults = '2';

  const handleSearchWithParams = async (params: {
    checkIn: string;
    numNights: number;
    adults: number;
    childrenAges: number[];
  }) => {
    if (!hotel) return;
    
    setIsSearching(true);
    setPendingSearchParams(params);

    try {
      // Check availability by calling the API
      const { data, error } = await supabase.functions.invoke('iterpec-hotel-details', {
        body: {
          destinationId: hotel.destination_id,
          checkIn: params.checkIn,
          numNights: params.numNights,
          adults: params.adults,
          childrenAges: params.childrenAges,
          hotelId: parseInt(hotel.hotel_id),
        },
      });

      if (error) throw error;

      const childrenAgesParam = params.childrenAges.length > 0 
        ? `&childrenAges=${params.childrenAges.join(',')}` 
        : '';

      // Check if hotel has availability
      if (data?.TotalHotelResults === 0 || !data?.Hotels || data.Hotels.length === 0) {
        // No availability - show modal
        setIsSearchModalOpen(false);
        setIsNoAvailabilityModalOpen(true);
      } else {
        // Has availability - navigate to hotel detail page
        setIsSearchModalOpen(false);
        if (hotel.destination_id) {
          navigate(`/hoteles/${hotel.hotel_id}?destinationId=${hotel.destination_id}&checkIn=${params.checkIn}&numNights=${params.numNights}&adults=${params.adults}${childrenAgesParam}`);
        } else {
          navigate(`/hoteles?hotelId=${hotel.hotel_id}&checkIn=${params.checkIn}&numNights=${params.numNights}&adults=${params.adults}${childrenAgesParam}`);
        }
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      toast({
        title: t('common.error'),
        description: t('errors.searchFailed'),
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleStayOnPage = () => {
    setIsNoAvailabilityModalOpen(false);
    setPendingSearchParams(null);
  };

  const handleGoToResults = () => {
    if (pendingSearchParams && hotel?.destination_id) {
      const childrenAgesParam = pendingSearchParams.childrenAges.length > 0 
        ? `&childrenAges=${pendingSearchParams.childrenAges.join(',')}` 
        : '';
      navigate(`/hoteles?destinationId=${hotel.destination_id}&checkIn=${pendingSearchParams.checkIn}&numNights=${pendingSearchParams.numNights}&adults=${pendingSearchParams.adults}${childrenAgesParam}`);
    }
    setIsNoAvailabilityModalOpen(false);
    setPendingSearchParams(null);
  };

  const handleCheckAvailability = () => {
    setIsSearchModalOpen(true);
  };

  // Get subtitle icon based on text content
  const getSubtitleIcon = (subtitleText: string) => {
    const text = subtitleText.toLowerCase();
    if (text.includes('ubicación') || text.includes('localização') || text.includes('location')) return MapPin;
    if (text.includes('habitacion') || text.includes('quarto') || text.includes('room')) return Bed;
    if (text.includes('restaurante') || text.includes('gastronom') || text.includes('cocina') || text.includes('comida') || text.includes('dining')) return UtensilsCrossed;
    if (text.includes('piscina') || text.includes('pool') || text.includes('agua')) return Waves;
    if (text.includes('spa') || text.includes('bienestar') || text.includes('wellness') || text.includes('relax')) return Sparkles;
    if (text.includes('gimnasio') || text.includes('fitness') || text.includes('deporte') || text.includes('sport')) return Dumbbell;
    if (text.includes('playa') || text.includes('praia') || text.includes('beach')) return Umbrella;
    if (text.includes('jardín') || text.includes('jardim') || text.includes('garden') || text.includes('naturaleza')) return TreePine;
    if (text.includes('montaña') || text.includes('vista') || text.includes('mountain') || text.includes('view')) return Mountain;
    if (text.includes('servicio') || text.includes('serviço') || text.includes('service') || text.includes('comodidad')) return Heart;
    if (text.includes('seguridad') || text.includes('segurança') || text.includes('security')) return Shield;
    if (text.includes('niño') || text.includes('criança') || text.includes('child') || text.includes('familia')) return Baby;
    if (text.includes('accesib') || text.includes('movilidad')) return Accessibility;
    if (text.includes('parking') || text.includes('estacion') || text.includes('aparcamiento')) return ParkingCircle;
    if (text.includes('wifi') || text.includes('internet') || text.includes('conectividad')) return Wifi;
    if (text.includes('desayuno') || text.includes('café') || text.includes('breakfast')) return Coffee;
    if (text.includes('transporte') || text.includes('traslado') || text.includes('aeropuerto')) return Plane;
    if (text.includes('horario') || text.includes('check') || text.includes('hora')) return Clock;
    if (text.includes('edificio') || text.includes('hotel') || text.includes('instalacion')) return Building2;
    if (text.includes('atracci') || text.includes('turismo') || text.includes('cultural')) return Landmark;
    return Info;
  };

  // Render description with formatted subtitles
  const renderDescription = (text: string) => {
    return text.split('\n').map((line, index) => {
      // Check if line is a subtitle (wrapped in **)
      const subtitleMatch = line.match(/^\*\*(.+)\*\*$/);
      if (subtitleMatch) {
        const IconComponent = getSubtitleIcon(subtitleMatch[1]);
        return (
          <h3 key={index} className="flex items-center gap-2 font-semibold text-foreground mt-4 mb-2 first:mt-0">
            <IconComponent className="h-4 w-4 text-primary shrink-0" />
            {subtitleMatch[1]}
          </h3>
        );
      }
      // Regular line
      return line ? <p key={index} className="mb-2 last:mb-0">{line}</p> : <br key={index} />;
    });
  };

  if (isLoading) {
    return <HotelDetailLoading />;
  }

  if (!hotel) {
    return null;
  }

  return (
    <>
      <HreflangTags path={`/hotel/${slug}`} />
{faqSchema 
        ? <SchemaOrg schema={[organizationSchema, webSiteSchema, breadcrumbSchema!, hotelSchema!, faqSchema]} />
        : <SchemaOrg schema={[organizationSchema, webSiteSchema, breadcrumbSchema!, hotelSchema!].filter(Boolean)} />
      }
      <Navbar />
      
      <main id="main-content" className="min-h-screen bg-background">
        {/* Header Section */}
        <div className="border-b bg-card animate-fade-in">
          <div className="container mx-auto px-4 py-6">
            <Breadcrumb
              items={[
                { label: t('nav.home'), href: "/" },
                { label: t('nav.hotels'), href: "/hoteles" },
                { label: hotel.name },
              ]}
            />

            <div className="mt-4 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div>
                {/* Stars */}
                {hotel.star_rating && (
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: hotel.star_rating }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                )}
                
                {/* Hotel Name */}
                <h1 className="text-3xl lg:text-4xl font-bold tracking-tight mb-3">
                  {hotel.name}
                </h1>
                
                {/* Address with map link */}
                {hotel.address && (
                  <button 
                    onClick={() => hasCoordinates && setIsMapOpen(true)}
                    className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors group"
                  >
                    <MapPin className="h-4 w-4" />
                    <span className="group-hover:underline">{hotel.address}</span>
                    {hasCoordinates && (
                      <span className="text-sm text-primary font-medium">- {t('hotels.viewOnMap')}</span>
                    )}
                  </button>
                )}
              </div>

              {/* CTA */}
              <div className="flex items-center gap-4">
                <Button size="lg" onClick={handleCheckAvailability} className="whitespace-nowrap">
                  <Search className="h-4 w-4 mr-2" />
                  {t('hotelStatic.searchAvailability')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          {/* Image Gallery - Full Width */}
          {images.length > 0 && (
            <div className="mb-8 animate-fade-in relative" style={{ animationDelay: '0.1s', animationFillMode: 'backwards' }}>
              {/* Recommended Hotel Badge */}
              {manualInfo && (
                <div className="absolute top-4 left-4 z-10">
                  <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-lg px-4 py-2 text-sm font-semibold gap-2">
                    <Star className="h-4 w-4 fill-white" />
                    {t('hotelDetail.recommendedHotel')}
                  </Badge>
                </div>
              )}
              <EnhancedImageGallery images={images} productName={hotel.name} />
            </div>
          )}

          {/* Main Content with Sidebar */}
          <div className="grid lg:grid-cols-3 gap-8 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'backwards' }}>
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Property Highlights */}
              {propertyHighlights.length > 0 && (
                <div className="flex flex-wrap gap-4">
                  {propertyHighlights.map((highlight, index) => (
                    <div key={index} className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg">
                      <highlight.icon className="h-5 w-5 text-primary" />
                      <span className="text-sm font-medium">{highlight.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Description */}
              {cleanedDescription && (
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t('hotelDetail.aboutHotel')}</h2>
                    <div className={`overflow-hidden transition-all duration-300 ${
                      isDescriptionExpanded ? 'max-h-none' : 'max-h-32'
                    }`}>
                      <div className="text-muted-foreground leading-relaxed">
                        {renderDescription(cleanedDescription)}
                      </div>
                    </div>
                    {cleanedDescription.length > 250 && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                        className="mt-2 px-0 h-auto"
                      >
                        {isDescriptionExpanded ? (
                          <>
                            {t('hotelDetail.showLess')}
                            <ChevronUp className="h-4 w-4 ml-1" />
                          </>
                        ) : (
                          <>
                            {t('hotelDetail.showMore')}
                            <ChevronDown className="h-4 w-4 ml-1" />
                          </>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Manual Review */}
              {manualInfo && (
                <HotelManualInfoSection manualInfo={manualInfo} />
              )}

              {/* Amenities */}
              {amenityCategories.length > 0 && (
                <HotelAmenities categories={amenityCategories} />
              )}

              {/* Location Map */}
              {hasCoordinates && (
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold mb-4">{t('hotelDetail.location')}</h2>
                    <HotelStaticMap
                      latitude={hotel.latitude!}
                      longitude={hotel.longitude!}
                      hotelName={hotel.name}
                      onClick={() => setIsMapOpen(true)}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Link to destination guide (if published) */}
              <HotelStaticDestinationLink iterpecDestinationId={hotel.destination_id} />

              {/* FAQ Section */}
              <HotelStaticFAQ
                hotelId={hotel.hotel_id}
                hotelName={cleanHotelName}
                destinationName={[hotel.city, hotel.country].filter(Boolean).join(", ") || null}
                address={hotel.address}
                city={hotel.city}
                country={hotel.country}
                phone={hotel.phone}
                amenityNames={[
                  ...hotel.amenities,
                  ...amenityCategories.flatMap((c) => c.amenities.map((a) => a.name)),
                ]}
              />
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <Card className="shadow-lg border-2">
                  <CardContent className="p-6">
                    {/* Check Availability Message */}
                    <div className="text-center mb-6">
                      <div className="flex items-center justify-center gap-2 text-muted-foreground mb-2">
                        <Calendar className="h-5 w-5" />
                        <Users className="h-5 w-5" />
                      </div>
                      <p className="text-lg font-medium text-foreground">
                        {t('hotelStatic.checkAvailability')}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('hotelStatic.selectDatesAndGuests')}
                      </p>
                    </div>

                    <Button 
                      className="w-full" 
                      size="lg"
                      onClick={handleCheckAvailability}
                    >
                      <Search className="h-4 w-4 mr-2" />
                      {t('hotelStatic.searchAvailability')}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Interactive Map Modal - Lazy loaded */}
      {hasCoordinates && (
        <LazyInteractiveHotelMap
          isOpen={isMapOpen}
          onClose={() => setIsMapOpen(false)}
          latitude={hotel.latitude!}
          longitude={hotel.longitude!}
          hotelName={hotel.name}
        />
      )}

      {/* Search Modal */}
      <SearchEditModal
        open={isSearchModalOpen}
        onOpenChange={setIsSearchModalOpen}
        currentCheckIn={defaultCheckIn}
        currentNumNights={defaultNumNights}
        currentAdults={defaultAdults}
        currentChildrenAges=""
        onSearch={handleSearchWithParams}
        isSearching={isSearching}
      />

      {/* No Availability Modal */}
      <NoAvailabilityModal
        open={isNoAvailabilityModalOpen}
        onOpenChange={setIsNoAvailabilityModalOpen}
        onStayOnPage={handleStayOnPage}
        onGoToResults={handleGoToResults}
      />

      <Footer />
    </>
  );
};

export default HotelStaticPage;
